import React, { useMemo } from "react";
import "./OtpInput.css";

export type Props = {
  value: string;
  valueLength: number;
  onChange: (value: string) => void;
};

export default function OtpInput({ value, valueLength, onChange }: Props) {
  const valueItems = useMemo(() => {
    const RE_DIGIT = /^\d+$/; // ✅ moved inside

    const safeValue = value || "";
    const valueArray = safeValue.split("");
    const items: string[] = [];

    for (let i = 0; i < valueLength; i++) {
      const char = valueArray[i];
      items.push(RE_DIGIT.test(char) ? char : "");
    }

    return items;
  }, [value, valueLength]); // ✅ no warning now

  const focusToNextInput = (target: HTMLElement) => {
    const next = target.nextElementSibling as HTMLInputElement | null;
    if (next) next.focus();
  };

  const focusToPrevInput = (target: HTMLElement) => {
    const prev = target.previousElementSibling as HTMLInputElement | null;
    if (prev) prev.focus();
  };

  const inputOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number
  ) => {
    const target = e.target;
    const targetValue = target.value.trim();

    if (!/^\d*$/.test(targetValue)) return;

    const safeValue = value || "";

    if (targetValue.length === 1) {
      const newValue =
        safeValue.substring(0, idx) +
        targetValue +
        safeValue.substring(idx + 1);

      onChange(newValue);
      focusToNextInput(target);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, valueLength);

    if (digits.length > 0) {
      onChange(digits);
    }
  };

  const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const idx = Array.from(target.parentNode!.children).indexOf(target);
    const safeValue = value || "";

    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusToNextInput(target);
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusToPrevInput(target);
    }

    if (e.key === "Backspace") {
      e.preventDefault();

      if (target.value !== "") {
        const newValue =
          safeValue.substring(0, idx) +
          "" +
          safeValue.substring(idx + 1);
        onChange(newValue);
      } else {
        focusToPrevInput(target);
      }
    }
  };

  const inputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <div className="otp-group">
      {valueItems.map((digit, idx) => (
        <input
          key={idx}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={`Digit ${idx + 1} of ${valueLength}`}
          autoComplete={idx === 0 ? "one-time-code" : "off"}
          maxLength={1}
          className="otp-input"
          value={digit}
          onChange={(e) => inputOnChange(e, idx)}
          onKeyDown={inputOnKeyDown}
          onFocus={inputOnFocus}
          onPaste={handlePaste}
          id={`otp-input-${idx + 1}`}
        />
      ))}
    </div>
  );
}